import React, {
  useState,
  useCallback,
  ReactNode,
  ReactElement,
  useMemo,
} from "react";

import {
  Table,
  Button,
  Menu,
  Dropdown,
  Tooltip,
  ConfigProvider,
  TablePaginationConfig,
} from "antd";
import {ColumnType} from "antd/lib/table";
import {SizeType} from "antd/lib/config-provider/SizeContext";

import {ColumnHeightOutlined} from "@ant-design/icons";
import {Key} from "antd/lib/table/interface";

import {Card} from "../ui";
import "moment/locale/pt-br";
import {Container} from "./styles";

interface ITableProps {
  columns: ColumnType<any>[];
  data: any[];
  loading?: boolean;
  title?: ReactNode;
  footer?: (currentPageData: any) => any;
  pagination?: TablePaginationConfig | any;
  onChange?: (pagination: TablePaginationConfig | null | undefined) => void;
  onRow?: (record: any, index?: number) => React.HTMLAttributes<HTMLElement>;
  selectable?: boolean;
  setSelectedRowKeys?: (rowKeys: Key[], rows: any[]) => void;
  extraActions?: ReactElement;
  extraFilters?: ReactElement;
  className?: string;
}

const ParametrizedTable: React.FC<ITableProps> = ({
                                                    columns,
                                                    data,
                                                    loading = false,
                                                    title,
                                                    onChange,
                                                    footer,
                                                    pagination,
                                                    onRow,
                                                    selectable,
                                                    setSelectedRowKeys: setParentSelectedRowKeys,
                                                    extraActions,
                                                    className,
                                                    extraFilters
                                                    // tabsFilters,
                                                    // primaryColor,
                                                    // tableStyle,
                                                    // maxHeight,
                                                    // mainExtraAction,
                                                  }) => {
  const [tableSize, setTableSize] = useState<SizeType>("small");
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const handleSelectTableSize = useCallback(({key}) => {
    setTableSize(key);
  }, []);

  const tableSizeMenu = (
    <Menu onClick={handleSelectTableSize}>
      <Menu.Item key="small">Pequeno</Menu.Item>
      <Menu.Item key="middle">Médio</Menu.Item>
      <Menu.Item key="large">Grande</Menu.Item>
    </Menu>
  );

  const onSelectedRowsChange = (
    newSelectedRowKeys: Key[],
    newSelectedRows: any[]
  ): void => {
    if (setParentSelectedRowKeys) {
      setParentSelectedRowKeys(newSelectedRowKeys, newSelectedRows);
    }
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectedRowsChange,
  };

  const handleTableChange = (paginationConf: TablePaginationConfig): void => {
    if (onChange) {
      onChange(paginationConf);
    }
  };

  const tableColumnsWidth = useMemo(() => {
    let sumColumnsWidth = 0;
    columns.forEach((column) => {
      if (!column.title) {
        sumColumnsWidth += 40;
      } else {
        const titleString = column.title as string;
        sumColumnsWidth += titleString.length * 20;
      }
    });
    return sumColumnsWidth;
  }, [columns]);

  return (
    <Container>
      <Card
        bodyStyle={{padding: 0}}
        extra={
          <div className="card-content">
            {extraActions}
            <Dropdown overlay={tableSizeMenu}>
              <Tooltip placement="top" title="Altura das linhas">
                <Button
                  icon={<ColumnHeightOutlined/>}
                  size="large"
                  type="link"
                  // style={{ color: primaryColor }}
                />
              </Tooltip>
            </Dropdown>
            {/* {mainExtraAction} */}
          </div>
        }
        title={title}
      >
        {extraFilters}
        {/* {tabsFilters} */}
        <ConfigProvider>
          <Table
            className={`${className} table--striped`}
            columns={columns.map((col) => {
              // If something else that is not a string, width attribute is necessary
              if (typeof col.title === "string") {
                // eslint-disable-next-line no-param-reassign
                col.width = col.width ?? `${col.title.length + 5}ch`;
              }
              return col;
            })}
            // align="center"
            dataSource={data}
            loading={loading}
            footer={footer}
            pagination={pagination}
            rowClassName={() => (onRow ? "table-row-pointer" : "")}
            rowKey={({id}) => id}
            rowSelection={selectable ? rowSelection : undefined}
            scroll={{
              x: data?.length > 0 ? "max-content" : tableColumnsWidth,
              // y: maxHeight,
            }}
            sticky
            size={tableSize}
            // style={tableStyle}
            tableLayout="auto"
            onChange={handleTableChange}
            onRow={onRow}
          />
        </ConfigProvider>
      </Card>
    </Container>
  );
};

export default ParametrizedTable;
